// Pure helpers extracted from history-viewer.ts so they can be
// exercised by property tests without dragging in WASM, DOM, or GSAP.
// No imports from `./history-viewer` — that module owns side effects
// and a transitive chain through codemirror → hooks/ci-pipeline/helpers
// → .dox/index that pure tests cannot resolve.

import type { MotionKind, TransitionLineKind } from "./history-viewer-types";

// Same shape as the renderer-side TransitionLine. Re-declared here so
// the pure module owns no DOM/animation deps. The renderer re-exports
// from this module to keep one source of truth.
export interface PureTransitionLine {
  readonly kind: TransitionLineKind;
  readonly oldText: string;
  readonly newText: string;
}

// Minimal payload shape needed by sameTimeline. Production payloads
// carry plenty more (commit metadata, active hash, etc) — we only
// need the commit-list spine for timeline equivalence.
// `content` is the loaded blob content for that commit. Paradox emits
// it as `string | null` on `HistoryCommitFrame`; we accept the same
// nullable shape (plus the absent case) so the dox-validated payload
// flows through `sameTimeline` without a refining cast at the call site.
export interface PureCommitFrame {
  readonly hash: string;
  readonly content?: string | null;
}

export interface PurePayloadSpine {
  readonly commits: readonly PureCommitFrame[];
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export const splitLines = (text: string): string[] => {
  if (text === "") return [""];
  return text.replace(/\r\n/g, "\n").split("\n");
};

// Pair up corresponding lines from two contents and classify each
// row. Pure; deterministic; symmetric under swap of inputs *with* a
// corresponding swap of oldText/newText fields.
export const buildTransitionLines = (
  fromText: string,
  toText: string,
): PureTransitionLine[] => {
  const fromLines = splitLines(fromText);
  const toLines = splitLines(toText);
  const count = Math.max(fromLines.length, toLines.length);
  const lines: PureTransitionLine[] = [];

  for (let index = 0; index < count; index += 1) {
    const oldText = fromLines[index] ?? "";
    const newText = toLines[index] ?? "";
    const kind: TransitionLineKind =
      oldText === newText
        ? "unchanged"
        : oldText === ""
          ? "added"
          : newText === ""
            ? "removed"
            : "changed";
    lines.push({ kind, oldText, newText });
  }

  return lines;
};

// Map a commit index into [0, 1] scroll progress. Inverse:
// `Math.round(progress * (count - 1))` recovers the source index when
// the input was a valid in-range integer.
export const progressFromIndex = (index: number, count: number): number => {
  if (count <= 1) return 0;
  return clamp(index, 0, count - 1) / (count - 1);
};

// ---------------------------------------------------------------------------
// F* bridge codec: optional line index ↔ u32
// ---------------------------------------------------------------------------
//
// The WASM ABI reserves 0 to mean "no line" (the F* side's `option int`
// returning `None`). Real indices shift by 1 on the wire.
//
// Inverse pair: decodeIndex(encodeIndex(x)) === x for every input in
// `null | nat`. encodeIndex(null) === 0 by definition.

export const encodeIndex = (index: number | null): number =>
  index === null ? 0 : index + 1;

export const decodeIndex = (value: number): number | null =>
  value === 0 ? null : value - 1;

// Two payloads describe the same timeline iff every commit hash and
// inline content matches in order. Null/null is *false* deliberately —
// "no payload" doesn't equal "no other payload" for the live viewer's
// purposes; treat that as a fresh boot.
export const sameTimeline = (
  left: PurePayloadSpine | null,
  right: PurePayloadSpine | null,
): boolean => {
  if (!left || !right) return false;
  if (left.commits.length !== right.commits.length) return false;

  return left.commits.every((commit, index) => {
    const other = right.commits[index];
    return (
      !!other && commit.hash === other.hash && commit.content === other.content
    );
  });
};

// `buildMotionLines` + `buildTransitionFrame` live in
// `./history-viewer-frame-pure` so they can import the F* bridge
// without re-introducing a cycle here (this module is imported by the
// bridge for its index codec). Re-exported below for backwards
// compatibility with existing call sites.
export {
  buildMotionLines,
  buildTransitionFrame,
  type PureMotionLine,
  type PureCommitForFrame,
  type PureTransitionFrame,
  type PureTransitionFrameLine,
} from "./history-viewer-frame-pure";

// Minimal motion-line shape: enough to compute newline highlighting.
// The full motion line above carries extra fields used by the renderer.
export interface MotionLineLite {
  readonly kind: MotionKind;
  readonly toIndex: number | null;
}

// Given the previous and current commit's content alongside the
// already-computed motion lines, derive the set of *target* line
// indices that should be marked "added" (gutter highlight) on the
// new commit's render. Pure — every input is by value, every output
// is freshly constructed.
//
// `motionLines` is supplied by the caller because the production diff
// runs inside the F*/WASM bridge; tests can pass a trivial reference
// computation. The boundary at this seam is deliberate: keep the diff
// engine swappable, keep the post-processing testable.
export const computeNewlineHighlighting = (
  prevContent: string | null,
  _content: string,
  motionLines: readonly MotionLineLite[],
): { addedSet: ReadonlySet<number> } => {
  if (prevContent === null) return { addedSet: new Set<number>() };

  const addedSet = new Set(
    motionLines
      .filter((ml) => ml.kind === "added" && ml.toIndex !== null)
      .map((ml) => ml.toIndex as number),
  );

  return { addedSet };
};
